import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

export interface PasswordInputWithToggleProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Accessible label for the show/hide toggle button. */
  showLabel?: string;
  hideLabel?: string;
  /** Optional error message — when present, the input renders with destructive styles. */
  error?: string;
  /** Optional trailing icon (e.g. green check when confirm-password matches). */
  trailingSlot?: React.ReactNode;
}

export const PasswordInputWithToggle = React.forwardRef<
  HTMLInputElement,
  PasswordInputWithToggleProps
>(
  (
    {
      className,
      showLabel = 'Show password',
      hideLabel = 'Hide password',
      error,
      trailingSlot,
      autoComplete = 'new-password',
      ...inputProps
    },
    ref
  ) => {
    const [visible, setVisible] = React.useState(false);

    return (
      <div className="relative">
        <input
          ref={ref}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          aria-invalid={error ? 'true' : undefined}
          className={cn(
            'flex h-10 w-full rounded-md border border-input bg-background pl-3 pr-20 py-2 text-sm ring-offset-background',
            'placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            error && 'border-destructive focus-visible:ring-destructive',
            className
          )}
          {...inputProps}
        />

        <div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-2">
          {trailingSlot && (
            <div className="flex items-center" aria-hidden="true">
              {trailingSlot}
            </div>
          )}
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            disabled={inputProps.disabled}
            aria-label={visible ? hideLabel : showLabel}
            aria-pressed={visible}
            className={cn(
              'rounded-sm p-1 text-muted-foreground hover:text-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
            tabIndex={inputProps.disabled ? -1 : 0}
          >
            {visible ? (
              <EyeOff className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Eye className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>
    );
  }
);

PasswordInputWithToggle.displayName = 'PasswordInputWithToggle';
